import React, { useState, useEffect, useCallback } from 'react';
import { getTokenForRequest } from '../../utils/apiAuth';
import './GamificationModal.css';

/**
 * Gamification Modal Component - Bootstrap 5 with centered positioning and solid background
 */
const GamificationModal = ({ data, onClose }) => {
  const [classes, setClasses] = useState([]);
  const [moduleSemesterId, setModuleSemesterId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Helper function to encode token for API calls
   */
  const encodeToken = useCallback((token) => {
    return token.toString();
  }, []);

  /**
   * Load classes data using GetClasses API
   */
  useEffect(() => {
    const loadClasses = async () => {
      if (!data.moduleSemesterId) return;

      setLoading(true);
      setError(null);
      setModuleSemesterId(data.moduleSemesterId);

      try {
        const token = getTokenForRequest();
        const encodedToken = encodeToken(token);

        const response = await fetch('./webservices/Organization.svc/GetClasses', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token: encodedToken,
            modulesemesterid: data.moduleSemesterId
          }),
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const classesData = await response.json();
        
        if (classesData && Array.isArray(classesData)) {
          setClasses(classesData);
        } else {
          setError('Invalid response format');
        }
      } catch (err) {
        setError('Error loading classes: ' + err.message);
        console.error('Error loading classes:', err);
      } finally {
        setLoading(false);
      }
    };

    loadClasses();
  }, [data.moduleSemesterId, encodeToken]);

  /**
   * Toggle leaderboard for a specific class
   */
  const toggleLeaderBoard = useCallback(async (targetClass, leaderboardValue) => {
    setLoading(true);
    setError(null);
    
    try {
      const token = getTokenForRequest();
      const classId = parseInt(targetClass.ID);
      const leaderboardInt = parseInt(leaderboardValue);
      
      if (window.GamificationUtilities) {
        const helper = new window.GamificationUtilities();
        
        helper.SetClassLeaderboard(classId, token, leaderboardInt, 
          (result) => {
            setClasses(prevClasses => 
              prevClasses.map(cls => 
                cls.ID === targetClass.ID 
                  ? { ...cls, Leaderboard: leaderboardValue }
                  : cls
              )
            );
            setLoading(false);
          },
          (status, xhr) => {
            if (window.CheckForAuthError && window.CheckForAuthError(status)) {
              return;
            }
            setError(status.statusText || "Error setting leaderboard for class");
            setLoading(false);
          }
        );
      } else {
        setError('GamificationUtilities not available');
        setLoading(false);
      }
    } catch (err) {
      setError('Error updating leaderboard: ' + err.message);
      setLoading(false);
    }
  }, []);

  /**
   * Set module leaderboard for all classes
   */
  const setModuleLeaderboard = useCallback(async (moduleId, leaderboardValue) => {
    setLoading(true);
    setError(null);
    
    try {
      const token = getTokenForRequest();
      
      if (window.GamificationUtilities) {
        const helper = new window.GamificationUtilities();
        
        helper.SetMoudleLeaderboard(moduleId, token, leaderboardValue,
          (result) => {
            setClasses(prevClasses => 
              prevClasses.map(cls => ({ ...cls, Leaderboard: leaderboardValue }))
            );
            setLoading(false);
          },
          (status, xhr) => {
            if (window.CheckForAuthError && window.CheckForAuthError(status)) {
              return;
            }
            setError(status.statusText || "Error setting leaderboard for module");
            setLoading(false);
          }
        );
      } else {
        setError('GamificationUtilities not available');
        setLoading(false);
      }
    } catch (err) {
      setError('Error updating module leaderboard: ' + err.message);
      setLoading(false);
    }
  }, []);

  /**
   * Toggle score for a specific class
   */
  const toggleScore = useCallback(async (targetClass) => {
    setLoading(true);
    setError(null);
    
    try {
      const token = getTokenForRequest();
      const classId = targetClass.ID;
      
      if (window.GamificationUtilities) {
        const helper = new window.GamificationUtilities();
        
        if (targetClass.Score) {
          helper.UnsetClassScore(classId, token,
            (result) => {
              setClasses(prevClasses => 
                prevClasses.map(cls => 
                  cls.ID === targetClass.ID 
                    ? { ...cls, Score: !cls.Score }
                    : cls
                )
              );
              setLoading(false);
            },
            (status, xhr) => {
              if (window.CheckForAuthError && window.CheckForAuthError(status)) {
                return;
              }
              setError(status.statusText || "Error unsetting score for class");
              setLoading(false);
            }
          );
        } else {
          helper.SetClassScore(classId, token,
            (result) => {
              setClasses(prevClasses => 
                prevClasses.map(cls => 
                  cls.ID === targetClass.ID 
                    ? { ...cls, Score: !cls.Score }
                    : cls
                )
              );
              setLoading(false);
            },
            (status, xhr) => {
              if (window.CheckForAuthError && window.CheckForAuthError(status)) {
                return;
              }
              setError(status.statusText || "Error setting score for class");
              setLoading(false);
            }
          );
        }
      } else {
        setError('GamificationUtilities not available');
        setLoading(false);
      }
    } catch (err) {
      setError('Error toggling score: ' + err.message);
      setLoading(false);
    }
  }, []);

  return (
    <div className="modal-backdrop-custom" onClick={onClose}>
      <div className="modal show d-block" tabIndex="-1">
        <div className="modal-dialog modal-lg modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
          <div className="modal-content">
            
            {/* HEADER - Bootstrap 5 modal header */}
            <div className="modal-header">
              <h4 className="modal-title">Gamification Classes</h4>
              <button 
                type="button" 
                className="btn-close-x" 
                onClick={onClose}
                aria-label="Close"
              >×</button>
            </div>
            
            {/* BODY - Bootstrap 5 modal body */}
            <div className="modal-body">
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}
              
              <div className="container-fluid">
                <div className="row">
                  <div className="col-12">
                    <div className="table-responsive">
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            <td>Class</td>
                            <td>Score</td>
                            <td>Leaderboard</td>
                          </tr>
                        </thead>
                        <tbody>
                          {/* Module-level controls row */}
                          <tr>
                            <td></td>
                            <td>
                              <button 
                                className="btn btn-primary"
                                onClick={() => setModuleLeaderboard(moduleSemesterId, 1)}
                                disabled={loading}
                              >
                                All Leaderboard Ranked By CLASS
                              </button>
                            </td>
                            <td>
                              <button 
                                className="btn btn-info"
                                onClick={() => setModuleLeaderboard(moduleSemesterId, 2)}
                                disabled={loading}
                              >
                                All Leaderboard Ranked By MODULE
                              </button>
                            </td>
                          </tr>
                          
                          {/* Class rows */}
                          {classes.map((cls) => (
                            <tr key={cls.ID}>
                              <td>{cls.Name}</td>
                              
                              {/* Score column */}
                              <td>
                                <button 
                                  className={`btn ${cls.Score ? 'btn-success' : 'btn-danger'}`}
                                  onClick={() => toggleScore(cls)}
                                  disabled={loading}
                                >
                                  Can {!cls.Score && 'Not'} See Score
                                </button>
                              </td>
                              
                              {/* Leaderboard column */}
                              <td>
                                {cls.Leaderboard === 1 && (
                                  <button 
                                    className="btn btn-success"
                                    onClick={() => toggleLeaderBoard(cls, 0)}
                                    disabled={loading}
                                  >
                                    See leaderboard ranked by class
                                  </button>
                                )}
                                
                                {cls.Leaderboard === 2 && (
                                  <button 
                                    className="btn btn-success"
                                    onClick={() => toggleLeaderBoard(cls, 0)}
                                    disabled={loading}
                                  >
                                    See leaderboard ranked by module
                                  </button>
                                )}
                                
                                {cls.Leaderboard === 0 && (
                                  <button 
                                    className="btn btn-danger"
                                    onClick={() => toggleLeaderBoard(cls, 1)}
                                    disabled={loading}
                                  >
                                    Can not see leaderboard
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      
                      {loading && (
                        <div className="d-flex justify-content-center py-3">
                          <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* FOOTER - Bootstrap 5 modal footer */}
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={onClose}
              >
                Close
              </button>
            </div>
            
          </div>
        </div>
      </div>
    </div>
  );
};

export default GamificationModal; 